import {
  enumBoxStyleSearch,
  enumBSFlagOptions,
  kBoxOverlayRule,
  kBoxStyleRule,
  kBranchStyleOverlayRule,
  kDynHighlightRule,
  kFieldOverlayRule,
  kHighlightingRule,
  kInheritedRule,
  kMasterPageRule,
  kMasterSCID,
  kPositionBoxOverlayRule,
  kPositionBoxStyleRule,
  kPositionFieldOverlayRule
} from "../constants";
import { PreProcessBranchRule } from "../utils/PreProcessBranchRule";
import type { QBoxLayout } from "./QBoxLayout";
import type { QBoxStylesManager } from "./QBoxStylesManager";
import type { QChartBox } from "./QChartBox";
import type { QChartTemplate } from "./QChartTemplate";
import type { QChartTemplateRule } from "./QChartTemplateRule";
import type { QSubChart } from "./QSubChart";
import type { QSubChartMgr } from "./QSubChartMgr";
import type { QXDoc } from "./QXDoc";
import type { QXWSMgr } from "./QXWSMgr";

export class QChartTemplateProcessor {
  constructor(
    private targetDoc: QXDoc,
    private chartTemplate: QChartTemplate,
    boxList: null | QChartBox[],
    private applyToSubChart: false | number,
    private wsMgr: QXWSMgr,
    private boxStyleMgr: QBoxStylesManager
  ) {
    this.boxList = boxList ?? [];
  }

  private didFormattingChange = false;
  private didHighlight = false;
  private theMasterTemplate: false | string = false;
  private boxList: QChartBox[];

  Apply(): { doRecalc: boolean; applyMaster: string | false } {
    const template = this.chartTemplate;
    const ruleList = template.fRuleList;

    const scm = this.targetDoc.GetSubChartMgr();
    const curSC = scm?.GetCurrentSubChart();

    if (
      !scm ||
      !ruleList ||
      curSC == null ||
      curSC.GetSubChartID() === -1 ||
      curSC.GetSubChartParentID() === -1 ||
      curSC.GetSubChartID() === kMasterSCID
    ) {
      return { doRecalc: false, applyMaster: false };
    }

    for (const rule of ruleList) {
      if (rule.HasActiveAction(kBranchStyleOverlayRule)) {
        this.ApplyBranchStyle(rule, scm);
      }

      if (rule.HasActiveAction(kBoxStyleRule)) {
        this.ApplyBoxStyle(rule, scm, kBoxStyleRule);
      }

      if (rule.HasActiveAction(kPositionBoxStyleRule)) {
        this.ApplyBoxStyle(rule, scm, kPositionBoxStyleRule);
      }

      if (
        rule.HasActiveAction(kBoxOverlayRule) ||
        rule.HasActiveAction(kFieldOverlayRule) ||
        rule.HasActiveAction(kPositionBoxOverlayRule) ||
        rule.HasActiveAction(kPositionFieldOverlayRule)
      ) {
        this.ApplyBoxRules(rule, scm);
      }

      if (rule.HasActiveAction(kMasterPageRule)) {
        this.ApplyMasterRule(rule, scm, curSC);
      }

      if (rule.HasActiveAction(kInheritedRule)) {
        this.ApplyInheritedRule(rule);
      }

      if (rule.HasActiveAction(kHighlightingRule) || rule.HasActiveAction(kDynHighlightRule)) {
        this.ApplyHighlightRule(rule, scm);
      }
    }

    return { doRecalc: this.didFormattingChange, applyMaster: this.theMasterTemplate };
  }

  ShouldApplyToThisSubChart() {
    const curDoc = this.wsMgr.GetDoc();
    return curDoc === this.targetDoc;
  }

  ApplyBranchStyle(rule: QChartTemplateRule, scm: QSubChartMgr) {
    let doPreProcess = true;

    const bsOption = rule.GetBranchStyleFlag();
    switch (bsOption) {
      case enumBSFlagOptions.assistleft:
      case enumBSFlagOptions.assistright:
        //case enumBSFlagOptions.leftpeerassist:
        //case enumBSFlagOptions.rightpeerassist:
        doPreProcess = false;
        break;
    }

    if (this.ShouldApplyToThisSubChart()) {
      const sList = rule.GetMatchingBoxes(this.boxList, this.targetDoc);

      let sc = scm.GetActiveSubChart();
      if (sc != null && doPreProcess) {
        sc.fBranchStyleOverride = true;
      }

      if (doPreProcess) {
        PreProcessBranchRule(rule, scm, sList);
      } else {
        this.wsMgr.ApplyLayoutToBoxList(null, sList, rule, false);
      }

      this.didFormattingChange = true;
    }

    if (this.applyToSubChart !== false) {
      const targetDoc = this.targetDoc;
      const nodeList = targetDoc.GetSCNodeList();
      const scList = rule.GetMatchingSCNodes(nodeList, this.applyToSubChart, scm, targetDoc);

      if (doPreProcess) {
        PreProcessBranchRule(rule, scm, scList);
      } else {
        targetDoc.UpdateLayoutForSCNodes(null, scList, rule);
      }
    }
  }

  ApplyBoxStyle(rule: QChartTemplateRule, scm: QSubChartMgr, action: string) {
    const boxStyleID = rule.GetBoxStyleID(action);
    const template = this.chartTemplate;
    const boxStyleMgr = this.boxStyleMgr;
    const targetDoc = this.targetDoc;
    const wsMgr = this.wsMgr;

    let matchLayout: null | QBoxLayout = null;

    if (template.fRootLayout && boxStyleID == template.fRootLayout.GetBoxStyleID()) {
      matchLayout = template.fRootLayout;
    } else {
      matchLayout = boxStyleMgr.GetLayoutByID(boxStyleID, true, enumBoxStyleSearch.allSets);
    }

    let cloneLayout = boxStyleMgr.CloneLayout(matchLayout, targetDoc);

    //an actual box layout
    if (this.ShouldApplyToThisSubChart()) {
      const boxList = this.boxList;

      const matchedBoxes = rule.GetMatchingBoxes(boxList, targetDoc);
      const relatedBoxes = rule.GetRelatedBoxes(matchedBoxes, targetDoc);

      const targetBoxes = relatedBoxes.filter(function (aBox) {
        return aBox.GetLayout().GetContainerType() === cloneLayout?.GetContainerType();
      });

      wsMgr.ApplyLayoutToBoxList(cloneLayout, targetBoxes, null, false);

      if (targetBoxes.length > 0) {
        this.didFormattingChange = true;
      }
    }

    if (this.applyToSubChart !== false) {
      const nodeList = targetDoc.GetSCNodeList();
      let scList = rule.GetMatchingSCNodes(nodeList, this.applyToSubChart, scm, targetDoc);
      scList = scList.filter(function (aNode) {
        return aNode.GetLayout().GetContainerType() === cloneLayout.GetContainerType();
      });

      targetDoc.UpdateLayoutForSCNodes(cloneLayout, scList, null);
    }
  }

  ApplyBoxRules(rule: QChartTemplateRule, scm: QSubChartMgr) {
    const targetDoc = this.targetDoc;

    if (this.ShouldApplyToThisSubChart()) {
      const boxList = this.boxList;
      const wsMgr = this.wsMgr;

      const matchedBoxes = rule.GetMatchingBoxes(boxList, targetDoc);
      const relatedBoxes = rule.GetRelatedBoxes(matchedBoxes, targetDoc);

      wsMgr.ApplyLayoutToBoxList(null, relatedBoxes, rule, false);

      if (relatedBoxes.length > 0) {
        this.didFormattingChange = true;
      }
    }

    if (this.applyToSubChart !== false) {
      const nodeList = targetDoc.GetSCNodeList();
      const scList = rule.GetMatchingSCNodes(nodeList, this.applyToSubChart, scm, targetDoc);
      targetDoc.UpdateLayoutForSCNodes(null, scList, rule);
    }
  }

  ApplyMasterRule(rule: QChartTemplateRule, scm: QSubChartMgr, curSC: QSubChart) {
    const targetDoc = this.targetDoc;

    // A rule has acted on the subchart, so flag the other ones as needing update.
    if (this.ShouldApplyToThisSubChart() && rule.GetMasterName() != null) {
      curSC.forceTemplate = true;

      const canvasRef = this.wsMgr.fCanvasPanel;
      const currSCBoxes = canvasRef?.GetSubchartBoxList(curSC, null) ?? [];

      for (let j = 0; j < currSCBoxes.length; j++) {
        if (scm.IsTopOfSubChart(currSCBoxes[j].GetDataObjID())) {
          const sList = rule.GetMatchingBoxes([currSCBoxes[j]], targetDoc);
          if (sList.length > 0) {
            this.theMasterTemplate = rule.GetMasterName();
          }

          break;
        }
      }
    }
  }

  ApplyInheritedRule(rule: QChartTemplateRule) {
    // Holy recursion batman!
    const inheritedTemplateId = rule.GetInheritTemplate();

    // Prevent infinite loop from self-referencing linked view
    if (inheritedTemplateId === this.chartTemplate.fTemplateID) {
      return;
    }

    // Find the template
    const wsMgr = this.wsMgr;
    const ctm = wsMgr.GetChartTemplatesMgr();
    let inheritedTemplate = ctm?.FindTemplateByIDAllSets(inheritedTemplateId, true)?.template;

    // Attempt to find by name if not found by ID. This is for backwards compat only
    if (!inheritedTemplate) {
      inheritedTemplate = ctm?.FindTemplateByNameAllSets(inheritedTemplateId, true);
    }

    if (inheritedTemplate && this.ShouldApplyToThisSubChart()) {
      const boxList = this.boxList;
      const targetDoc = this.targetDoc;
      const applyToSubChart = this.applyToSubChart;
      const boxStyleMgr = this.boxStyleMgr;

      const sList = rule.GetMatchingBoxes(boxList, targetDoc);

      const processor = new QChartTemplateProcessor(
        targetDoc,
        inheritedTemplate,
        sList,
        applyToSubChart,
        wsMgr,
        boxStyleMgr
      );
      const inheritResult = processor.Apply();

      if (inheritResult["doRecalc"]) {
        this.didFormattingChange = true;
      }
      if (inheritResult["applyMaster"] !== false) {
        //NOT !inheritResult['applyMaster'] - it could take the value of "" which we don't want loose type comparison for.
        this.theMasterTemplate = inheritResult["applyMaster"];
      }
    }
  }

  ApplyHighlightRule(rule: QChartTemplateRule, scm: QSubChartMgr) {
    const template = this.chartTemplate;
    const boxList = this.boxList;
    const applyToSubChart = this.applyToSubChart;
    const targetDoc = this.targetDoc;
    const nodeList = targetDoc.GetSCNodeList();

    let highlightType = "template";
    if (template.fTemplateID === "__internalDynHighlight__") {
      highlightType = "dynamic";
    }

    const isDynamic = highlightType === "dynamic";
    const idx = template.GetIndexOfRule(rule);

    let theName = `${template.fTemplateID}${idx}`;
    if (isDynamic) {
      theName = rule.fHighlightName;
    }

    if (this.ShouldApplyToThisSubChart()) {
      if (!this.didHighlight && isDynamic) {
        for (const aBoxIdx in boxList) {
          boxList[aBoxIdx].GetLayout().ClearHighlightInfo(highlightType);
        }
      }

      if (!isDynamic || rule.fHighlightActive) {
        const sList = rule.GetMatchingBoxes(boxList, targetDoc);
        const highlightColor = rule.GetRuleHighlightColor();

        for (const aBoxIdx in sList) {
          const theBox = sList[aBoxIdx];
          if (!isDynamic || rule.fHighlightActive) {
            theBox.SetHighlightInfo(theName, highlightColor, highlightType);
          }
        }
      }
    }

    if (!this.didHighlight && isDynamic) {
      for (const aNodeIdx in nodeList) {
        const theBox = nodeList[aNodeIdx];
        if (theBox.GetSubChartID() !== applyToSubChart) {
          theBox.GetLayout().ClearHighlightInfo();
        }
      }
    }

    if (applyToSubChart !== false) {
      if (!isDynamic || rule.fHighlightActive) {
        const highlightColor = rule.GetRuleHighlightColor();
        const scList = rule.GetMatchingSCNodes(nodeList, applyToSubChart, scm, targetDoc);
        for (const aBoxIdx in scList) {
          const theBox = scList[aBoxIdx];
          theBox.GetLayout().SetHighlightInfo(theName, highlightColor, highlightType);
        }
      }
    }

    this.didFormattingChange = true;
    this.didHighlight = true;
  }
}
